import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export type ResearchType = 'dissertation' | 'replication' | 'other';

export interface Research {
  id: string;
  slug: string;
  name: string;
  short_name: string;
  type: ResearchType;
  lead: string;
  researchers: string[];
  year: number | null;
  description: string;
  source_url: string;
  doi: string;
  status: 'active' | 'archived';
  is_default: boolean;
  dataset_ids: string[];
  emotion_class_ids: string[];
  preprocessing_active_keys: string[];
  created_at: string;
  updated_at: string;
}

export interface ResearchPayload {
  name: string;
  short_name?: string;
  type?: ResearchType;
  lead?: string;
  researchers?: string[];
  year?: number;
  description?: string;
  source_url?: string;
  doi?: string;
}

export interface DatasetMapping {
  dataset_id: string;
  label_mapping: Record<string, string>;
}

@Injectable({ providedIn: 'root' })
export class ResearchApiService {
  private http = inject(HttpClient);
  private base = `${environment.apiUrl}/researches`;

  getAll(): Observable<Research[]> {
    return this.http.get<Research[]>(this.base);
  }

  getOne(id: string): Observable<Research> {
    return this.http.get<Research>(`${this.base}/${id}`);
  }

  create(body: ResearchPayload): Observable<{ id: string }> {
    return this.http.post<{ id: string }>(this.base, body);
  }

  update(id: string, body: Partial<Research> & { type?: ResearchType }): Observable<Research> {
    return this.http.patch<Research>(`${this.base}/${id}`, body);
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}`);
  }

  seed(): Observable<{ upserted: number }> {
    return this.http.post<{ upserted: number }>(`${this.base}/seed`, {});
  }

  /** Persist the active research for the current user. */
  setActiveResearch(researchId: string | null): Observable<{ active_research_id: string | null }> {
    return this.http.put<{ active_research_id: string | null }>(
      `${environment.apiUrl}/users/me/active-research`,
      { research_id: researchId },
    );
  }

  listDatasetMappings(researchId: string): Observable<{ mappings: DatasetMapping[] }> {
    return this.http.get<{ mappings: DatasetMapping[] }>(
      `${this.base}/${researchId}/dataset-mappings`,
    );
  }

  getDatasetMapping(researchId: string, datasetId: string): Observable<{ label_mapping: Record<string, string> }> {
    return this.http.get<{ label_mapping: Record<string, string> }>(
      `${this.base}/${researchId}/dataset-mappings/${datasetId}`,
    );
  }

  putDatasetMapping(
    researchId: string,
    datasetId: string,
    labelMapping: Record<string, string>,
  ): Observable<{ ok: boolean }> {
    return this.http.put<{ ok: boolean }>(
      `${this.base}/${researchId}/dataset-mappings/${datasetId}`,
      { label_mapping: labelMapping },
    );
  }
}
